import React from 'react';
import {FlexRow, Modal, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../forecast-app/shared/components/modals/types';

export interface DiscardReportDraftModalPayload {
	onConfirm: () => void;
	// Whether the draft being discarded would also revert the report's filter. False whenever there
	// is no open draft, or the draft predates this field. Picks between the modal body that mentions
	// the filter and the one that doesn't, so the copy never claims a filter revert that will not
	// actually happen.
	filterWillRevert: boolean;
}

/** Opens the discard confirmation. Discard is not reversible from the surface, and it reverts more
 * than the last edit, so it is always confirmed. */
export function showDiscardReportDraftModal(payload: DiscardReportDraftModalPayload): void {
	showModal({type: MODAL_TYPE.DISCARD_REPORT_DRAFT, ...payload});
}

export function DiscardReportDraftModal({
	closeModal,
	onConfirm,
	filterWillRevert,
}: ModalProps<DiscardReportDraftModalPayload>): JSX.Element {
	const {formatMessage} = useIntl();

	const handleConfirm = (): void => {
		onConfirm();
		closeModal();
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{formatMessage({id: 'ai_report_builder.discard_modal_title'})}</Modal.Title>
			<Modal.Content>
				<Text>
					{formatMessage({
						id: filterWillRevert
							? 'ai_report_builder.discard_modal_body_with_filter'
							: 'ai_report_builder.discard_modal_body',
					})}
				</Text>
				<FlexRow>
					<Modal.PrimaryFooterButton onPress={handleConfirm} dataCy="confirm-discard-report-draft">
						<FormattedMessage id={'ai_report_builder.discard'} />
					</Modal.PrimaryFooterButton>
					<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
						<FormattedMessage id="common.cancel" />
					</Modal.SecondaryFooterButton>
				</FlexRow>
			</Modal.Content>
		</Modal>
	);
}
